import { indexedDBStorage } from 'components/WorkspaceEditor/persistDb'
import {
    EditorActionBarStoreType,
    VisibilitySettingsStates,
} from 'components/WorkspaceEditor/types/editorActionBar.type'

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'
import { useEditorActionBarStore } from './useEditorActionBarStore'

/**
 * Creates a zustand store for managing workspace editor action bar state
 */
export const createEditorActionBarStore = (projectId: string) =>
    create<EditorActionBarStoreType>()(
        persist(
            (set) => ({
                isVisibilitySettingsMenuDisplayed: false,
                visibilitySettingValues: {
                    areNotesVisible: true,
                    isInactiveContentVisible: true,
                    areTrackChangesVisible: true,
                },
                /** actions/setters */
                actions: {
                    setIsVisibilitySettingsMenuDisplayed: (
                        displayed: boolean
                    ) => {
                        set({ isVisibilitySettingsMenuDisplayed: displayed })
                    },

                    setVisibilitySettingValues: (
                        value: VisibilitySettingsStates
                    ) => {
                        set({ visibilitySettingValues: value })
                    },
                },
            }),

            {
                name: `editorActionBar-${projectId}`,
                // versioning for future migrations
                version: 1.1,
                storage: indexedDBStorage,
                // partial persistence: explicitly choose which state properties to persist
                partialize: (state) => ({
                    visibilitySettingValues: state.visibilitySettingValues,
                }),
            }
        )
    )
/** Zustand selector for getting the actions/setters. */
export const useEditorActionBarActions = () =>
    useEditorActionBarStore((state) => state.actions)

export const useIsVisibilitySettingsMenuDisplayed = () =>
    useEditorActionBarStore((state) => state.isVisibilitySettingsMenuDisplayed)

export const useVisibilitySettingValues = () =>
    useEditorActionBarStore((state) => state.visibilitySettingValues)

//  * @param selector - A function that takes the store state and returns the value to be selected.
//  * @returns The selected value from the store.

export const useShallowEditorActionBar = <T>(
    selector: (state: EditorActionBarStoreType) => T
): T => useEditorActionBarStore(useShallow(selector))

export const createEditorActionBarStoreContainer = (
    editorActionBarStore: Map<
        string,
        ReturnType<typeof createEditorActionBarStore>
    >
) => {
    return (projectId: string) => {
        // Check if projectId exists in indexDB instance
        if (!editorActionBarStore?.has(projectId)) {
            editorActionBarStore?.set(
                projectId,
                createEditorActionBarStore(projectId)
            )
        }
        return editorActionBarStore?.get(projectId)!
    }
}
